import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, FileDown, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMaster } from "@/lib/useMaster";
import { formatDateTime, formatIDR } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { productMap, warehouseMap } = useMaster();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ customer_name: "", customer_phone: "", note: "" });

  const openEdit = () => {
    setEditForm({ customer_name: inv.customer_name, customer_phone: inv.customer_phone || "", note: inv.note || "" });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, editForm);
      setInv(data);
      setEditOpen(false);
      toast.success("Invoice updated");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  useEffect(load, [load]);

  const markPaid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/pay`);
      setInv(data);
      toast.success("Invoice marked as paid");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const markUnpaid = async () => {
    if (!window.confirm(`Change invoice ${inv.inv_no} back to Unpaid? It will reappear in Receivables.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/unpay`);
      setInv(data);
      toast.success("Invoice changed back to unpaid");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete invoice ${inv.inv_no}? The stock will be restored to its FIFO batches. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted — stock restored");
      navigate("/penjualan");
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  if (!inv) {
    return (
      <div className="grid h-64 place-items-center" data-testid="invoice-detail-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-blue-600" />
      </div>
    );
  }

  const wh = warehouseMap[inv.warehouse_id];
  const margin = inv.subtotal - inv.total_cost;

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/penjualan")} data-testid="btn-back-invoice" className="rounded-sm border border-zinc-200 bg-white p-2 text-zinc-500 transition-colors hover:text-zinc-900">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-mono text-2xl font-bold tracking-tight text-zinc-900" data-testid="invoice-no">{inv.inv_no}</h1>
              <StatusBadge status={inv.status} />
            </div>
            <p className="mt-1 text-sm text-zinc-500">Created {formatDateTime(inv.created_at)} · by <span className="font-medium text-zinc-700" data-testid="invoice-created-by">{inv.created_by_name}</span>{inv.edited_by_name ? <span data-testid="invoice-edited-by"> · edited by {inv.edited_by_name}</span> : null}{inv.unpaid_by_name ? <span className="text-amber-600" data-testid="invoice-unpaid-by"> · reverted to unpaid by {inv.unpaid_by_name}</span> : null}</p>
            {inv.note && (
              <p className="mt-1 text-sm text-zinc-600" data-testid="invoice-note">Note: {inv.note}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {inv.status === "belum_lunas" && !inv.deleted && (
            <Button data-testid="btn-mark-paid" disabled={busy} onClick={markPaid} className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700">
              <BadgeCheck className="mr-2 h-4 w-4" /> Mark as Paid
            </Button>
          )}
          {inv.status === "lunas" && !inv.deleted && user.role === "owner" && (
            <Button data-testid="btn-mark-unpaid" disabled={busy} onClick={markUnpaid} variant="outline" className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50">
              <RotateCcw className="mr-2 h-4 w-4" /> Mark as Unpaid
            </Button>
          )}
          {user.role === "owner" && !inv.deleted && (
            <Button data-testid="btn-edit-invoice" disabled={busy} variant="outline" onClick={openEdit} className="rounded-sm border-zinc-300 text-zinc-600">
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
          )}
          {user.role === "owner" && !inv.deleted && (
            <Button data-testid="btn-delete-invoice" disabled={busy} variant="outline" onClick={handleDelete} className="rounded-sm border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
          <Button
            data-testid="btn-print-invoice"
            variant="outline"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`, "_blank")}
            className="rounded-sm border-zinc-300"
          >
            <FileDown className="mr-2 h-4 w-4" /> Print PDF
          </Button>
        </div>
      </div>

      {inv.deleted && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="invoice-deleted-banner">
          This invoice has been deleted{inv.deleted_by_name ? ` by ${inv.deleted_by_name}` : ""}{inv.deleted_at ? ` on ${formatDateTime(inv.deleted_at)}` : ""}. Stock has been restored. Record kept for audit.
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Invoice {inv.inv_no}</DialogTitle>
            <DialogDescription className="sr-only">Edit invoice customer details</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Customer Name</Label>
              <Input data-testid="edit-invoice-customer" required value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input data-testid="edit-invoice-phone" value={editForm.customer_phone} onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })} className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input data-testid="edit-invoice-note" value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} placeholder="Optional note" className="rounded-sm border-zinc-300" />
            </div>
            <p className="text-xs text-zinc-400">Only customer details can be edited — items and prices are locked because FIFO stock was already consumed. To change items, delete this invoice and create a new one.</p>
            <Button data-testid="edit-invoice-submit" type="submit" disabled={busy} className="w-full rounded-sm bg-blue-600 text-white hover:bg-blue-700">
              {busy ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="mx-auto max-w-4xl border border-zinc-200 bg-white p-8 md:p-12" data-testid="invoice-document">
        <div className="flex items-start justify-between border-b-2 border-blue-600 pb-6">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">HORIZON</h2>
            <p className="mt-1 text-sm text-zinc-500">Phone & Laptop Store</p>
          </div>
          <div className="text-right">
            <p className="font-heading text-xl font-bold tracking-tight text-zinc-900">INVOICE</p>
            <p className="mt-1 font-mono text-sm text-blue-600">{inv.inv_no}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-6 text-sm md:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400">Customer</p>
            <p className="mt-1 font-semibold text-zinc-900" data-testid="invoice-customer-name">{inv.customer_name}</p>
            <p className="text-zinc-500">{inv.customer_phone || "-"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400">Source Warehouse</p>
            <p className="mt-1 font-semibold text-zinc-900">{wh?.name}</p>
            <p className="text-zinc-500">{wh?.address}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400">Date</p>
            <p className="mt-1 text-zinc-700">{formatDateTime(inv.created_at)}</p>
          </div>
        </div>

        <table className="mt-6 w-full text-sm" data-testid="invoice-items-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-3 py-2.5 font-medium">Product</th>
              <th className="px-3 py-2.5 font-medium">FIFO Batch</th>
              <th className="px-3 py-2.5 text-right font-medium">Qty</th>
              <th className="px-3 py-2.5 text-right font-medium">Price</th>
              <th className="px-3 py-2.5 text-right font-medium">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, i) => (
              <tr key={i} className="border-b border-zinc-100 last:border-0">
                <td className="px-3 py-2.5 font-medium text-zinc-900">{productMap[it.product_id]?.name || "?"}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-zinc-500">
                  {(it.allocations || []).map((a) => `${a.batch_no} ×${a.qty}`).join(", ")}
                </td>
                <td className="tnum px-3 py-2.5 text-right text-zinc-700">{it.qty}</td>
                <td className="tnum px-3 py-2.5 text-right text-zinc-700">{formatIDR(it.sell_price)}</td>
                <td className="tnum px-3 py-2.5 text-right font-medium text-zinc-900">{formatIDR(it.qty * it.sell_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-2 text-sm">
            <div className="flex justify-between border-t-2 border-zinc-900 pt-3">
              <span className="font-bold text-zinc-900">Total</span>
              <span className="tnum font-bold text-zinc-900" data-testid="invoice-total">{formatIDR(inv.subtotal)}</span>
            </div>
            <div className="flex justify-between text-xs text-zinc-400">
              <span data-testid="invoice-cogs">COGS (FIFO): {formatIDR(inv.total_cost)}</span>
              <span className="tnum" data-testid="invoice-margin">Margin: {formatIDR(margin)}</span>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-6 text-center text-sm text-zinc-600">
          <div>
            <p>Sincerely,</p>
            <p className="mt-14 font-medium text-zinc-400">( _________________ )</p>
          </div>
          <div>
            <p>Customer,</p>
            <p className="mt-14 font-medium text-zinc-400">( _________________ )</p>
          </div>
        </div>
      </div>
    </div>
  );
}
